import { ReactComponent as DisconnectIcon } from "../../assets/icons/disconnect.svg";
import { ReactComponent as MicIcon } from "../../assets/icons/mic.svg";
import { ReactComponent as MicOffIcon } from "../../assets/icons/mic-off.svg";
import { useState, useEffect, useRef } from "react";

const MUTE_MIC_POPUP_DURATION_MS = 5000;

interface ActionButtonsProps {
  onDisconnect: () => void;
  className?: string;
  audioStream?: MediaStream | null;
  /** True once the call has actually started (post-countdown). Triggers the one-time mute hint popup. */
  sessionStarted?: boolean;
  /** Tenant-configurable override for how long the mute hint popup stays visible. Falls back to MUTE_MIC_POPUP_DURATION_MS. */
  muteMicPopupDurationMs?: number;
}

export function ActionButtons({ onDisconnect, className, audioStream, sessionStarted, muteMicPopupDurationMs }: ActionButtonsProps) {
  const [isMuted, setIsMuted] = useState(false);
  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [showMuteHint, setShowMuteHint] = useState(false);
  const hintShownRef = useRef(false);
  const popupDurationMs =
    typeof muteMicPopupDurationMs === "number" ? muteMicPopupDurationMs : MUTE_MIC_POPUP_DURATION_MS;

  // Show the "mute when not speaking" hint once per session, for 5s (or the tenant-configured duration), as soon as the call starts.
  useEffect(() => {
    if (!sessionStarted || hintShownRef.current) {
      setShowMuteHint(false);
      return;
    }
    setShowMuteHint(true);
    const timeout = setTimeout(() => {
      hintShownRef.current = true;
      setShowMuteHint(false);
    }, popupDurationMs);
    return () => clearTimeout(timeout);
  }, [sessionStarted, popupDurationMs]);

  // Get the audio track from the stream
  useEffect(() => {
    if (audioStream) {
      const tracks = audioStream.getAudioTracks();
      if (tracks.length > 0) {
        setAudioTrack(tracks[0]);
        setIsMuted(!tracks[0].enabled);
      }
    }
  }, [audioStream]);

  const handleMuteToggle = () => {
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);
    }
  };

  return (
    <div className={`z-[2] pointer-events-none justify-center w-full absolute bottom-12 items-start gap-7 flex ${className}`}>
      {/* End Call Button */}
      <div className="flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={onDisconnect}
          data-tutorial-end-call-button
          className="group pointer-events-auto"
          aria-label="End call"
        >
          <div
            className="w-14 h-14 rounded-full flex justify-center items-center group-hover:opacity-90 transition-colors shadow-md praxis-call-button-bg"
          >
            <DisconnectIcon className="w-10 h-10 [&_circle]:fill-transparent [&_path]:fill-white" />
          </div>
        </button>
        <span className="text-xs text-text-primary">End Call</span>
      </div>

      {/* Mute/Unmute Button */}
      <div className="relative flex flex-col items-center gap-1">
        {showMuteHint && (
          <div
            role="status"
            className="absolute bottom-full mb-2 left-0 whitespace-nowrap rounded-[36px] bg-white/60 px-3.5 py-1.5 text-center text-[10px] font-semibold leading-[1.3] text-black shadow-sm"
          >
            <p>Mute mic when not speaking to</p>
            <p>avoid picking up background noise</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleMuteToggle}
          data-tutorial-microphone-button
          className="group pointer-events-auto"
          aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
        >
          <div
            className={`w-14 h-14 rounded-full flex justify-center items-center group-hover:opacity-90 transition-colors shadow-md ${isMuted ? 'praxis-call-button-bg-muted' : 'praxis-call-button-bg'}`}
          >
            {isMuted ? (
              <MicOffIcon className="w-10 h-10 [&_circle]:fill-transparent [&_path]:fill-white" />
            ) : (
              <MicIcon className="w-10 h-10 [&_circle]:fill-transparent [&_path]:fill-white" />
            )}
          </div>
        </button>
        <span className="text-xs text-text-primary">{isMuted ? "Unmute" : "Mute"}</span>
      </div>
    </div>
  );
}
